import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';

// Extend window interface to register jQuery globally for the CDN plugin
declare global {
    interface Window {
        $: any;
        jQuery: any;
    }
}

// Assign jQuery to window globally to make it accessible to Summernote CDN script
window.jQuery = $;
window.$ = $;

// Polyfill for jQuery.type() which was removed in jQuery 4.0.0 but is required by Summernote
if (typeof $.type !== 'function') {
    const typePolyfill = (obj: any) => {
        if (obj == null) {
            return String(obj);
        }
        return typeof obj === 'object' || typeof obj === 'function'
            ? Object.prototype.toString.call(obj).slice(8, -1).toLowerCase()
            : typeof obj;
    };
    $.type = typePolyfill;
    window.jQuery.type = typePolyfill;
    window.$.type = typePolyfill;
}

// Polyfill for jQuery.isFunction() which was removed in jQuery 4.0.0
if (typeof $.isFunction !== 'function') {
    const isFunctionPolyfill = (obj: any) => typeof obj === 'function';
    $.isFunction = isFunctionPolyfill;
    window.jQuery.isFunction = isFunctionPolyfill;
    window.$.isFunction = isFunctionPolyfill;
}

// Polyfill for jQuery.isWindow() which was removed in jQuery 4.0.0
if (typeof $.isWindow !== 'function') {
    const isWindowPolyfill = (obj: any) => obj != null && obj === obj.window;
    $.isWindow = isWindowPolyfill;
    window.jQuery.isWindow = isWindowPolyfill;
    window.$.isWindow = isWindowPolyfill;
}

// Polyfill for jQuery.now() which was removed in jQuery 4.0.0
if (typeof $.now !== 'function') {
    const nowPolyfill = () => Date.now();
    $.now = nowPolyfill;
    window.jQuery.now = nowPolyfill;
    window.$.now = nowPolyfill;
}

// Polyfill for jQuery.camelCase() which was removed in jQuery 4.0.0
if (typeof $.camelCase !== 'function') {
    const camelCasePolyfill = (string: string) => {
        return string.replace(/-([a-z])/g, function (all, letter) {
            return letter.toUpperCase();
        });
    };
    $.camelCase = camelCasePolyfill;
    window.jQuery.camelCase = camelCasePolyfill;
    window.$.camelCase = camelCasePolyfill;
}

// Polyfill for jQuery.proxy() which was removed in jQuery 4.0.0
if (typeof $.proxy !== 'function') {
    const proxyPolyfill = function (fn: any, context: any, ...args: any[]) {
        if (typeof context === "string") {
            const tmp = fn[context];
            context = fn;
            fn = tmp;
        }
        if (typeof fn !== "function") {
            return undefined;
        }
        return fn.bind(context, ...args);
    };
    $.proxy = proxyPolyfill;
    window.jQuery.proxy = proxyPolyfill;
    window.$.proxy = proxyPolyfill;
}

// Polyfill for jQuery.trim() which was removed in jQuery 4.0.0
if (typeof $.trim !== 'function') {
    const trimPolyfill = (str: any) => str == null ? "" : String(str).trim();
    $.trim = trimPolyfill;
    window.jQuery.trim = trimPolyfill;
    window.$.trim = trimPolyfill;
}

interface SummernoteProps {
    value: string;
    onChange: (content: string) => void;
    placeholder?: string;
    minHeight?: number;
    maxHeight?: number;
}

const Summernote: React.FC<SummernoteProps> = ({ value, onChange, placeholder, minHeight = 350, maxHeight }) => {
    const editorRef = useRef<HTMLTextAreaElement>(null);
    const [loaded, setLoaded] = useState(false);
    const isInitializingRef = useRef(false);

    // Keep active value inside a ref to prevent unnecessary re-render triggers in callback
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        // Inject CSS đặt font mặc định Times New Roman cho vùng soạn thảo
        const injectDefaultFontStyle = () => {
            if (document.getElementById('summernote-default-font')) return;
            const style = document.createElement('style');
            style.id = 'summernote-default-font';
            style.textContent = `
                .note-editor .note-editable,
                .note-editor .note-editable p,
                .note-editor .note-editable div,
                .note-editor .note-editable span,
                .note-editor .note-editable td,
                .note-editor .note-editable th,
                .note-editor .note-editable li {
                    font-family: 'Times New Roman', Times, serif;
                }
                .note-editor .note-editable {
                    font-size: 13pt;
                }
                /* Ghi đè viền ô mặc định của summernote-lite (1px solid #ececec).
                   Đây chỉ là đường gợi ý khi soạn thảo, không được lưu vào HTML.
                   Ô nào có style border inline (vd: border: none) thì inline vẫn thắng. */
                .summernote-editor-wrapper .note-editor .note-editing-area .note-editable table td,
                .summernote-editor-wrapper .note-editor .note-editing-area .note-editable table th {
                    border: 1px dashed #e3e3e3;
                }
            `;
            document.head.appendChild(style);
        };

        // Load Summernote JS and CSS dynamically from CDN if not already loaded
        const loadAssets = async () => {
            injectDefaultFontStyle();
            if (document.getElementById('summernote-cdn-css')) {
                const scriptEl = document.getElementById('summernote-cdn-js') as HTMLScriptElement;
                if (scriptEl) {
                    if ((window as any).$ && (window as any).$.fn && (window as any).$.fn.summernote) {
                        setLoaded(true);
                    } else {
                        scriptEl.addEventListener('load', () => setLoaded(true));
                    }
                }
                return;
            }

            // Create Link tag for Summernote CSS
            const link = document.createElement('link');
            link.id = 'summernote-cdn-css';
            link.rel = 'stylesheet';
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/summernote/0.8.20/summernote-lite.min.css';
            document.head.appendChild(link);

            // Create Script tag for Summernote JS (lite version)
            const script = document.createElement('script');
            script.id = 'summernote-cdn-js';
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/summernote/0.8.20/summernote-lite.min.js';
            script.async = true;
            script.onload = () => {
                setLoaded(true);
            };
            document.body.appendChild(script);
        };

        loadAssets();
    }, []);

    useEffect(() => {
        if (!loaded || !editorRef.current || isInitializingRef.current) return;

        const $editor = (window as any).$(editorRef.current);

        isInitializingRef.current = true;

        // ---- Hàm làm sạch HTML khi paste từ Word ----
        function escapeHtml(text: string) {
            return String(text || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        }

        function unwrapElement(el: Element) {
            const parent = el.parentNode;
            if (!parent) return;
            while (el.firstChild) {
                parent.insertBefore(el.firstChild, el);
            }
            parent.removeChild(el);
        }

        function cleanStyle(styleText: string) {
            if (!styleText) return '';
            const allowedStyles = [
                'text-align', 'text-indent', 'font-weight', 'font-style',
                'text-decoration', 'color', 'background-color', 'font-size',
                'line-height', 'margin-left', 'margin-right', 'padding-left',
                // Style của bảng: giữ lại để 'border: none' từ Word không bị mất
                'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
                'border-collapse', 'border-color', 'border-style', 'border-width',
                'vertical-align', 'width', 'padding'
            ];
            return styleText
                .split(';')
                .map(s => s.trim())
                .filter(Boolean)
                .filter(rule => {
                    const parts = rule.split(':');
                    if (parts.length < 2) return false;
                    const prop = parts[0].trim().toLowerCase();
                    const val = parts.slice(1).join(':').trim().toLowerCase();
                    if (!allowedStyles.includes(prop)) return false;
                    if (prop.startsWith('mso-')) return false;
                    if (val.includes('mso-')) return false;
                    if (val.includes('expression')) return false;
                    if (val.includes('javascript:')) return false;
                    if (val.includes('url(')) return false;
                    return true;
                })
                .join('; ');
        }

        function isSafeHref(href: string) {
            if (!href) return false;
            return (
                href.startsWith('http://') || href.startsWith('https://') ||
                href.startsWith('mailto:') || href.startsWith('tel:') ||
                href.startsWith('#') || href.startsWith('/')
            );
        }

        function cleanWordHtml(html: string, plainText: string) {
            if (!html && !plainText) return '';
            if (!html && plainText) {
                return String(plainText)
                    .split(/\r?\n/)
                    .map(line => line.trim() ? '<p>' + escapeHtml(line) + '</p>' : '')
                    .join('');
            }

            html = String(html || '')
                .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
                .replace(/<!--[\s\S]*?-->/gi, '')
                .replace(/<xml[\s\S]*?<\/xml>/gi, '')
                .replace(/<style[\s\S]*?<\/style>/gi, '')
                .replace(/<(meta|link)[^>]*>/gi, '')
                .replace(/<\/?(?:o|w|m|v):[^>]*>/gi, '');

            const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
            const root = doc.body.firstChild as Element;

            const allowedTags = [
                'p', 'div', 'span', 'br',
                'b', 'strong', 'i', 'em', 'u', 's',
                'ul', 'ol', 'li',
                'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'blockquote', 'a'
            ];
            const removeTags = [
                'script', 'style', 'meta', 'link', 'xml',
                'iframe', 'object', 'embed', 'form', 'input', 'button'
            ];

            Array.from(root.querySelectorAll('*')).forEach(el => {
                const tag = el.tagName.toLowerCase();
                if (removeTags.includes(tag) || tag.includes(':')) {
                    el.remove();
                    return;
                }
                if (!allowedTags.includes(tag)) {
                    unwrapElement(el);
                    return;
                }
                Array.from(el.attributes).forEach(attr => {
                    const name = attr.name.toLowerCase();
                    const val = attr.value;
                    if (name.startsWith('on')) { el.removeAttribute(attr.name); return; }
                    if (name === 'class') { el.removeAttribute(attr.name); return; }
                    if (name === 'style') {
                        const cleaned = cleanStyle(val);
                        if (cleaned) el.setAttribute('style', cleaned);
                        else el.removeAttribute('style');
                        return;
                    }
                    if (tag === 'a' && name === 'href') {
                        if (isSafeHref(val)) el.setAttribute('href', val);
                        else el.removeAttribute('href');
                        return;
                    }
                    if (tag === 'a' && name === 'target') {
                        el.setAttribute('target', '_blank');
                        el.setAttribute('rel', 'noopener noreferrer');
                        return;
                    }
                    if ((tag === 'td' || tag === 'th') && (name === 'colspan' || name === 'rowspan')) return;
                    el.removeAttribute(attr.name);
                });
                if (tag === 'span' && el.attributes.length === 0) {
                    unwrapElement(el);
                }
            });

            let cleaned = root.innerHTML
                .replace(/<(p|div)>\s*<\/\1>/gi, '')
                .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
                .replace(/>\s+</g, '><')
                .trim();

            return cleaned;
        }
        // ---- End cleanWordHtml ----

        $editor.summernote({
            placeholder: placeholder || 'Nhập nội dung...',
            tabsize: 2,
            fontNames: [
                'Times New Roman', 'Arial', 'Arial Black', 'Calibri', 'Cambria',
                'Comic Sans MS', 'Courier New', 'Helvetica', 'Impact',
                'Tahoma', 'Verdana'
            ],
            fontNamesIgnoreCheck: ['Times New Roman', 'Calibri', 'Cambria'],
            // Xuất inline style font-family thay vì thẻ <font face> (dễ chuyển sang Word hơn)
            styleWithCSS: true,
            minHeight: minHeight,
            ...(maxHeight ? { maxHeight: maxHeight } : {}),
            toolbar: [
                ['style', ['style']],
                ['font', ['bold', 'underline', 'clear', 'italic', 'strikethrough', 'superscript', 'subscript']],
                ['fontname', ['fontname']],
                ['color', ['color']],
                ['para', ['ul', 'ol', 'paragraph']],
                ['table', ['table']],
                ['insert', ['link', 'picture', 'video']],
                ['view', ['codeview', 'help']]
            ],
            callbacks: {
                onPaste: function (e: any) {
                    e.preventDefault();
                    const originalEvent = e.originalEvent || e;
                    const clipboardData = originalEvent.clipboardData || (window as any).clipboardData;
                    if (!clipboardData) return;
                    const html = clipboardData.getData('text/html');
                    const plainText = clipboardData.getData('text/plain');
                    const cleaned = cleanWordHtml(html, plainText);
                    $editor.summernote('pasteHTML', cleaned);
                },
                onChange: (contents: string) => {
                    if (onChangeRef.current) {
                        onChangeRef.current(contents);
                    }
                }
            }
        });

        // Set initial value
        $editor.summernote('code', value || '');

        return () => {
            // Safely destroy Summernote when component unmounts
            try {
                $editor.summernote('destroy');
            } catch (err) {
                console.warn('Error destroying Summernote:', err);
            }
            isInitializingRef.current = false;
        };
    }, [loaded]);

    // Handle external value changes (like initial async data fetch)
    useEffect(() => {
        if (!loaded || !editorRef.current) return;
        const $editor = (window as any).$(editorRef.current);
        try {
            const currentCode = $editor.summernote('code');
            if (currentCode !== value) {
                $editor.summernote('code', value || '');
            }
        } catch (err) {
            // Ignore error if Summernote isn't fully initialized yet
        }
    }, [value, loaded]);

    return (
        <div className="summernote-editor-wrapper" style={{ minHeight: minHeight }}>
            <textarea ref={editorRef} style={{ display: 'none' }} />
        </div>
    );
};

export default Summernote;
